import React from 'react';
import Module from 'Components/Core/Module';
import MarketdataGroupSelector from './MarketdataGroupSelector';
import MarketdataChartTile, { formatLabel } from './MarketdataChartTile';
import MarketdataRangeSelector from './MarketdataRangeSelector';
import { setMarketdataForcedGroup, useMarketdataForcedGroup } from './marketdataForcedGroup';
import './Marketdata.css';

// Charts-Modul: dieselben Watchlists wie "Watchlist Live", aber als
// Kachel-Raster aus Mini-Charts statt einer Tabelle - ein Chart pro Symbol,
// gleiche Reihenfolge (sortorder) und gleiche Groesse wie bisher im
// Detail-Panel des Live-Moduls. Anders als "Watchlist Live" nicht live
// gepollt (Chart-Kerzen aendern sich nicht Sekunde fuer Sekunde relevant),
// sondern einmalig beim Oeffnen/Gruppenwechsel geladen (analog
// MarketdataChart.inc fuers Detail-Panel).
export default function MarketdataCharts(props) {

	const x = props.x;
	const [charts, setCharts] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState('');
	const [selectedGroupId, setSelectedGroupId] = React.useState(null);
	// Default "1m" statt "1T" (Nutzervorgabe, Chat vom 2026-08-07).
	const [range, setRange] = React.useState('1m');
	// Erzwungener Watchlist-Wechsel von aussen (siehe marketdataForcedGroup.js) -
	// "Charts anzeigen"-Icon in MarketdataWatchlist.js setzt das, BEVOR dieses
	// Modul aktiviert wird.
	const [forcedGroup, setForcedGroup] = React.useState(null);
	const consumeForcedGroup = useMarketdataForcedGroup(x, setForcedGroup);

	// Kein Batch-Endpunkt mehr fuer die Kerzen selbst (siehe MarketdataCharts.inc)
	// - erst die Symbolliste laden, dann pro Symbol EINZELN und PARALLEL genau
	// denselben Task wie das Detail-Panel in "Watchlist Live"
	// (MarketdataChart.inc) abfragen. Jede Kachel zeigt sich an, sobald ihr
	// eigener Request durch ist, statt dass das gesamte Raster auf die
	// langsamste Antwort wartet - und beide Module nutzen dieselbe
	// Kerzen-Logik (Einschwing-Puffer, Vortag-Anhang bei "1T") statt zweier
	// unterschiedlich verhaltender Code-Pfade (User-Feedback).
	function loadCharts() {
		if (selectedGroupId === null) {
			return;
		}
		setLoading(true);
		setError('');
		x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataCharts', { groupid: selectedGroupId }, (result) => {
			setLoading(false);
			if (result.data.returncode !== 1) {
				setError(result.data.content?.message || 'Charts konnten nicht geladen werden.');
				setCharts([]);
				return;
			}
			const symbols = result.data.content || [];
			setCharts(symbols.map((entry) => ({ ...entry, candles: null })));
			symbols.forEach((entry) => {
				x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataChart', { symbol: entry.symbol, range }, (chartResult) => {
					const rawCandles = chartResult.data.returncode === 1 ? (chartResult.data.content || []) : [];
					// Label schon HIER berechnen (nicht erst in MarketdataChartTile.js) -
					// der Live-Tick-Abgleich unten vergleicht gegen "time", das braucht
					// also von Anfang an einen Wert, sonst haengt der erste Live-Tick
					// nach dem Laden immer faelschlich einen neuen Punkt an, statt die
					// letzte historische Kerze zu aktualisieren.
					const candles = rawCandles.map((row) => ({ ...row, time: formatLabel(row.date, range) }));
					setCharts((current) => current.map((chart) => (
						chart.symbol === entry.symbol ? { ...chart, candles } : chart
					)));
				});
			});
		});
	}

	React.useEffect(() => {
		loadCharts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedGroupId, range]);

	// Charts live nachziehen statt nur beim Oeffnen/Wechseln einmalig zu laden -
	// nutzt denselben Socket.IO-Kanal wie "Watchlist Live". Fuer "1T" (5-Minuten-
	// Kerzen) auf 5-Minuten-Fenster runden und den Preis INNERHALB desselben
	// Fensters nur aktualisieren (nicht anhaengen), analog
	// MarketdataDetailPanel.js - ein reines Anhaengen jedes einzelnen Live-Ticks
	// erzeugte hier frueher eine Aufloesungs-Mismatch zur groben historischen
	// 5-Minuten-Reihe und verzerrte den Chart-Verlauf.
	const LIVE_BUCKET_MINUTES = { '1m': 1, '1T': 5 };
	const bucketMinutes = LIVE_BUCKET_MINUTES[range];
	const isLiveAppendable = bucketMinutes != null;
	React.useEffect(() => {
		if (!isLiveAppendable) {
			return;
		}
		function handleQuote(args) {
			// Nur verarbeiten, wenn "Charts" gerade das aktive/sichtbare Modul ist
			// (analog MarketdataQuotes.js) - unnoetige Re-Renders eines
			// unsichtbaren Kachel-Rasters vermeiden. Beim naechsten Aktivieren
			// laedt loadCharts() ohnehin den aktuellen Stand nach.
			if (x.wam.current !== props.id) {
				return;
			}
			const data = typeof args === 'string' ? JSON.parse(args) : args;
			if (data.price == null || data.timestamp == null) {
				return;
			}
			const bucketDate = new Date(data.timestamp);
			bucketDate.setMinutes(Math.floor(bucketDate.getMinutes() / bucketMinutes) * bucketMinutes, 0, 0);
			let time = bucketDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
			// "1T" zeigt seit dem Vortag-Anhang (siehe FmpClient::getChartForRange)
			// mehrere Handelstage - Datum mit anhaengen, analog zur Formatierung
			// in MarketdataChartTile.js.
			if (range === '1T') {
				time = bucketDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + ' ' + time;
			}
			setCharts((current) => {
				const index = current.findIndex((chart) => chart.symbol === data.symbol);
				if (index === -1) {
					// Symbol gehoert nicht zur aktuell angezeigten Watchlist (der
					// Kanal liefert Updates fuer ALLE Watchlists, siehe
					// marketdataWorker.mjs) - ignorieren.
					return current;
				}
				const chart = current[index];
				const candles = chart.candles || [];
				const lastCandle = candles[candles.length - 1];
				// Chat vom 2026-08-07 (Bugreport: "Veraenderung" in der Kachel wich
				// stark von Watchlist Live ab) - change/changePercentage kommen
				// jetzt live mit demselben Quote-Event mit (dieselbe echte
				// Tages-Veraenderung vs. Vortagesschluss wie in Watchlist Live),
				// nicht mehr aus einer eigenen Chart-Berechnung.
				const liveFields = { change: data.change ?? chart.change, changePercentage: data.changePercentage ?? chart.changePercentage };
				if (lastCandle && lastCandle.time === time) {
					if (lastCandle.close === data.price) {
						return current;
					}
					const updated = [...current];
					const updatedCandles = [...candles];
					updatedCandles[updatedCandles.length - 1] = { ...lastCandle, close: data.price };
					updated[index] = { ...chart, ...liveFields, candles: updatedCandles };
					return updated;
				}
				const updated = [...current];
				updated[index] = { ...chart, ...liveFields, candles: [...candles, { time, close: data.price }] };
				return updated;
			});
		}

		const connection = x.was.webSocket;
		if (connection) {
			connection.on('marketdata-quote', handleQuote);
		}
		return () => {
			if (connection) {
				connection.off('marketdata-quote', handleQuote);
			}
		};
	}, [isLiveAppendable, x, props.id, range, bucketMinutes]);

	return (
		<Module
			id={props.id}
			x={x}
			classes="module-marketdata-charts"
			title={props.title}
			display={props.display}
			displayTitle="d-block"
			onShow={consumeForcedGroup}
			titleToolbar={
				<span className="module-table-title-toolbar">
					<MarketdataGroupSelector x={x} id={props.id} onSelect={setSelectedGroupId} forcedGroup={forcedGroup} />
					<MarketdataRangeSelector range={range} onChange={setRange} />
					<i
						className="bi bi-pencil module-icon"
						title="Watchlist bearbeiten"
						onClick={() => {
							// Verwaltung soll dieselbe Watchlist zeigen, die hier gerade
							// ausgewaehlt ist (siehe marketdataForcedGroup.js).
							setMarketdataForcedGroup(x, selectedGroupId);
							x.wam.activate('marketdata-watchlist');
						}}
					/>
					<i
						className={'bi bi-arrow-repeat module-icon' + (loading ? ' marketdata-reload-spinning' : '')}
						title="Watchlist-Änderungen laden"
						onClick={loadCharts}
					/>
				</span>
			}
		>
			{loading && charts.length === 0 && <div className="marketdata-detail-hint">Charts werden geladen…</div>}
			{!loading && error && <div className="marketdata-detail-hint">{error}</div>}
			{!loading && !error && charts.length === 0 &&
				<div className="marketdata-empty">Keine Symbole in dieser Watchlist.</div>
			}
			{charts.length > 0 &&
				<div className="marketdata-charts-grid">
					{charts.map((chart) => (
						<MarketdataChartTile key={chart.symbol} symbol={chart.symbol} shortlabel={chart.shortlabel} candles={chart.candles} range={range} change={chart.change} changePercentage={chart.changePercentage} />
					))}
				</div>
			}
		</Module>
	)

};
